import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  ShoppingCart, 
  Package, 
  Building2, 
  Save,
  Search,
  Plus,
  Minus,
  Truck,
  FileText,
  Mail
} from "lucide-react";

const API = '/api';

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

export default function NovaEncomenda() {
  const navigate = useNavigate();
  const [farmacias, setFarmacias] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [armazenistas, setArmazenistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pulseTotal, setPulseTotal] = useState(false);

  const [formData, setFormData] = useState({
    farmacia_id: "",
    armazenista: "",
    observacoes: "",
    ano: new Date().getFullYear(),
    mes: MESES[new Date().getMonth()],
  });

  const [carrinho, setCarrinho] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [farmaciasRes, produtosRes, categoriasRes, armazenistasRes] = await Promise.all([
          axios.get(`${API}/farmacias`),
          axios.get(`${API}/produtos`),
          axios.get(`${API}/produtos/categorias`),
          axios.get(`${API}/armazenistas`),
        ]);

        setFarmacias(farmaciasRes.data);
        setProdutos(produtosRes.data);
        setCategorias(categoriasRes.data);
        setArmazenistas(armazenistasRes.data);

        const carrinhoInicial = {};
        produtosRes.data.forEach((prod) => {
          carrinhoInicial[prod.id] = {
            ...prod,
            quantidade: 0,
            oferta: 0,
          };
        });
        setCarrinho(carrinhoInicial);
      } catch (error) {
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const updateQuantidade = (produtoId, delta) => {
    setCarrinho((prev) => {
      const current = prev[produtoId];
      const newQtd = Math.max(0, current.quantidade + delta);
      return {
        ...prev,
        [produtoId]: { ...current, quantidade: newQtd },
      };
    });
    triggerPulse();
  };

  const updateOferta = (produtoId, delta) => {
    setCarrinho((prev) => {
      const current = prev[produtoId];
      const newOferta = Math.max(0, current.oferta + delta);
      return {
        ...prev,
        [produtoId]: { ...current, oferta: newOferta },
      };
    });
  };

  const setQuantidadeDirecta = (produtoId, value) => {
    const qty = parseInt(value) || 0;
    setCarrinho((prev) => ({
      ...prev,
      [produtoId]: { ...prev[produtoId], quantidade: Math.max(0, qty) },
    }));
    triggerPulse();
  };

  const setOfertaDirecta = (produtoId, value) => {
    const qty = parseInt(value) || 0;
    setCarrinho((prev) => ({
      ...prev,
      [produtoId]: { ...prev[produtoId], oferta: Math.max(0, qty) },
    }));
  };

  const triggerPulse = () => {
    setPulseTotal(true);
    setTimeout(() => setPulseTotal(false), 600);
  };

  const totalGeral = useMemo(() => {
    return Object.values(carrinho).reduce((acc, item) => {
      return acc + item.quantidade * item.pva;
    }, 0);
  }, [carrinho]);

  const itensNoCarrinho = useMemo(() => {
    return Object.values(carrinho).filter((item) => item.quantidade > 0 || item.oferta > 0);
  }, [carrinho]);

  const produtosFiltrados = useMemo(() => {
    if (!searchTerm) return produtos;
    const term = searchTerm.toLowerCase();
    return produtos.filter(
      (p) =>
        p.descricao.toLowerCase().includes(term) ||
        p.codigo.toLowerCase().includes(term)
    );
  }, [produtos, searchTerm]);

  const handleSubmit = async () => {
    if (!formData.farmacia_id) {
      toast.error("Selecione uma farmácia");
      return;
    }

    if (!formData.armazenista) {
      toast.error("Selecione um armazenista");
      return;
    }

    if (itensNoCarrinho.length === 0) {
      toast.error("Adicione pelo menos um produto");
      return;
    }

    setSaving(true);
    try {
      const itens = itensNoCarrinho.map((item) => ({
        produto_id: item.id,
        codigo: item.codigo,
        descricao: item.descricao,
        quantidade: item.quantidade,
        oferta: item.oferta,
        pva: item.pva,
        total: item.quantidade * item.pva,
      }));

      await axios.post(`${API}/encomendas`, {
        farmacia_id: formData.farmacia_id,
        armazenista: formData.armazenista,
        observacoes: formData.observacoes,
        ano: formData.ano,
        mes: formData.mes,
        itens,
      });

      toast.success("Encomenda criada com sucesso! Email enviado automaticamente.");
      navigate("/encomendas");
    } catch (error) {
      toast.error("Erro ao criar encomenda");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nova-encomenda-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">
          Nova Encomenda
        </h1>
        <p className="text-slate-500 mt-1">
          Selecione a farmácia, armazenista e adicione os produtos
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left - Products */}
        <div className="flex-1 space-y-4">
          {/* Pharmacy, Armazenista & Period Selection */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    Farmácia *
                  </Label>
                  <Select
                    value={formData.farmacia_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, farmacia_id: value })
                    }
                  >
                    <SelectTrigger data-testid="select-farmacia">
                      <SelectValue placeholder="Selecionar farmácia" />
                    </SelectTrigger>
                    <SelectContent>
                      {farmacias.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-slate-400" />
                    Armazenista (Transfer) *
                  </Label>
                  <Select
                    value={formData.armazenista}
                    onValueChange={(value) =>
                      setFormData({ ...formData, armazenista: value })
                    }
                  >
                    <SelectTrigger data-testid="select-armazenista">
                      <SelectValue placeholder="Selecionar armazenista" />
                    </SelectTrigger>
                    <SelectContent>
                      {armazenistas.map((a) => (
                        <SelectItem key={a} value={a}>
                          {a}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ano</Label>
                  <Select
                    value={formData.ano.toString()}
                    onValueChange={(value) =>
                      setFormData({ ...formData, ano: parseInt(value) })
                    }
                  >
                    <SelectTrigger data-testid="select-ano">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map((ano) => (
                        <SelectItem key={ano} value={ano.toString()}>
                          {ano}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Mês</Label>
                  <Select
                    value={formData.mes}
                    onValueChange={(value) =>
                      setFormData({ ...formData, mes: value })
                    }
                  >
                    <SelectTrigger data-testid="select-mes">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MESES.map((mes) => (
                        <SelectItem key={mes} value={mes}>
                          {mes}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  Observações / Anotações
                </Label>
                <Textarea
                  placeholder="Notas adicionais para esta encomenda..."
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  className="min-h-[80px]"
                  data-testid="input-observacoes"
                />
              </div>
            </CardContent>
          </Card>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Pesquisar produtos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-produtos"
            />
          </div>

          {/* Products by Category */}
          <Card>
            <Tabs defaultValue={categorias[0] || "Geral"}>
              <CardHeader className="border-b border-slate-100 pb-0">
                <TabsList className="w-full justify-start gap-1 bg-transparent p-0 flex-wrap">
                  {categorias.map((cat) => {
                    const bgColor = cat === "Geral" 
                      ? "data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:border-emerald-600"
                      : cat === "Linha Clínica"
                      ? "data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700 data-[state=active]:border-purple-600"
                      : "data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:border-blue-600";
                    return (
                      <TabsTrigger
                        key={cat}
                        value={cat}
                        className={`${bgColor} rounded-t-lg rounded-b-none border-b-2 border-transparent px-4 py-2 font-medium`}
                        data-testid={`tab-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {cat}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </CardHeader>

              {categorias.map((cat) => (
                <TabsContent key={cat} value={cat} className="mt-0">
                  <ScrollArea className="h-[400px]">
                    <div className="divide-y divide-slate-100">
                      {produtosFiltrados
                        .filter((p) => p.categoria === cat)
                        .map((produto) => {
                          const item = carrinho[produto.id];
                          return (
                            <div
                              key={produto.id}
                              className={`p-4 transition-colors ${
                                item?.quantidade > 0 ? "bg-blue-50/50" : "hover:bg-slate-50"
                              }`}
                              data-testid={`product-row-${produto.id}`}
                            >
                              {/* Mobile Layout */}
                              <div className="block sm:hidden space-y-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0 pr-2">
                                    <p className="font-medium text-slate-900 text-sm leading-tight">
                                      {produto.descricao}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="font-mono text-xs text-slate-500">
                                        {produto.codigo}
                                      </span>
                                      <span className="font-mono text-sm font-semibold text-blue-600">
                                        €{produto.pva.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  {/* Quantidade Mobile */}
                                  <div className="flex flex-col">
                                    <span className="text-xs text-slate-500 mb-1">Quantidade</span>
                                    <div className="flex items-center">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-11 w-11 touch-manipulation rounded-r-none border-r-0"
                                        onClick={() => updateQuantidade(produto.id, -1)}
                                        data-testid={`btn-minus-mobile-${produto.id}`}
                                      >
                                        <Minus className="w-5 h-5" />
                                      </Button>
                                      <Input
                                        type="number"
                                        value={item?.quantidade || 0}
                                        onChange={(e) =>
                                          setQuantidadeDirecta(produto.id, e.target.value)
                                        }
                                        className="h-11 text-center font-mono text-lg rounded-none border-x-0 flex-1"
                                        min="0"
                                      />
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-11 w-11 touch-manipulation rounded-l-none border-l-0"
                                        onClick={() => updateQuantidade(produto.id, 1)}
                                        data-testid={`btn-plus-mobile-${produto.id}`}
                                      >
                                        <Plus className="w-5 h-5" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Oferta Mobile */}
                                  <div className="flex flex-col">
                                    <span className="text-xs text-orange-500 mb-1">Oferta</span>
                                    <div className="flex items-center">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-11 w-11 touch-manipulation rounded-r-none border-r-0"
                                        onClick={() => updateOferta(produto.id, -1)}
                                      >
                                        <Minus className="w-5 h-5" />
                                      </Button>
                                      <Input
                                        type="number"
                                        value={item?.oferta || 0}
                                        onChange={(e) =>
                                          setOfertaDirecta(produto.id, e.target.value)
                                        }
                                        className="h-11 text-center font-mono text-lg rounded-none border-x-0 flex-1"
                                        min="0"
                                      />
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-11 w-11 touch-manipulation rounded-l-none border-l-0"
                                        onClick={() => updateOferta(produto.id, 1)}
                                      >
                                        <Plus className="w-5 h-5" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Desktop Layout */}
                              <div className="hidden sm:flex items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900 truncate">
                                    {produto.descricao}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1">
                                    <span className="font-mono text-xs text-slate-500">
                                      {produto.codigo}
                                    </span>
                                    <span className="font-mono text-sm font-semibold text-blue-600">
                                      €{produto.pva.toFixed(2)}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-4">
                                  {/* Quantidade Desktop */}
                                  <div className="flex flex-col items-center">
                                    <span className="text-xs text-slate-400 mb-1">Qtd</span>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => updateQuantidade(produto.id, -1)}
                                        data-testid={`btn-minus-${produto.id}`}
                                      >
                                        <Minus className="w-3 h-3" />
                                      </Button>
                                      <Input
                                        type="number"
                                        value={item?.quantidade || 0}
                                        onChange={(e) =>
                                          setQuantidadeDirecta(produto.id, e.target.value)
                                        }
                                        className="w-14 h-8 text-center font-mono"
                                        min="0"
                                        data-testid={`input-qtd-${produto.id}`}
                                      />
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => updateQuantidade(produto.id, 1)}
                                        data-testid={`btn-plus-${produto.id}`}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Oferta Desktop */}
                                  <div className="flex flex-col items-center">
                                    <span className="text-xs text-orange-500 mb-1">Oferta</span>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => updateOferta(produto.id, -1)}
                                      >
                                        <Minus className="w-3 h-3" />
                                      </Button>
                                      <Input
                                        type="number"
                                        value={item?.oferta || 0}
                                        onChange={(e) =>
                                          setOfertaDirecta(produto.id, e.target.value)
                                        }
                                        className="w-14 h-8 text-center font-mono"
                                        min="0"
                                        data-testid={`input-oferta-${produto.id}`}
                                      />
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => updateOferta(produto.id, 1)}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </ScrollArea>
                </TabsContent>
              ))}
            </Tabs>
          </Card>
        </div>

        {/* Right - Order Summary */}
        <div className="lg:w-96">
          <Card className="lg:sticky lg:top-8">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
                Resumo da Encomenda
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Info Summary */}
              {(formData.armazenista || formData.observacoes) && (
                <div className="p-4 border-b border-slate-100 bg-slate-50/50 space-y-2">
                  {formData.armazenista && (
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-blue-600" />
                      <span className="text-slate-600">Armazenista:</span>
                      <span className="font-medium text-slate-900">{formData.armazenista}</span>
                    </div>
                  )}
                  {formData.observacoes && (
                    <div className="text-sm">
                      <span className="text-slate-600">Obs:</span>
                      <span className="text-slate-700 ml-1">{formData.observacoes.slice(0, 50)}...</span>
                    </div>
                  )}
                </div>
              )}
              
              <ScrollArea className="h-[250px]">
                {itensNoCarrinho.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhum produto adicionado</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {itensNoCarrinho.map((item) => (
                      <div key={item.id} className="p-4">
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-slate-900 truncate">
                              {item.descricao}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {item.quantidade} × €{item.pva.toFixed(2)}
                              {item.oferta > 0 && (
                                <span className="text-orange-500 ml-2">
                                  + {item.oferta} oferta
                                </span>
                              )}
                            </p>
                          </div>
                          <span className="font-mono font-semibold text-sm text-slate-900">
                            €{(item.quantidade * item.pva).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* Email Notice */}
              <div className="px-4 py-2 bg-blue-50 border-y border-blue-100">
                <p className="text-xs text-blue-700 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Email enviado automaticamente após guardar
                </p>
              </div>

              {/* Total */}
              <div
                className={`border-t-2 border-blue-600 p-6 bg-blue-50/50 ${
                  pulseTotal ? "animate-pulse-blue" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-heading font-bold text-slate-800">Total</span>
                  <span 
                    className="font-mono font-bold text-2xl text-blue-700"
                    data-testid="total-value"
                  >
                    €{totalGeral.toFixed(2)}
                  </span>
                </div>
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  onClick={handleSubmit}
                  disabled={saving || itensNoCarrinho.length === 0 || !formData.farmacia_id || !formData.armazenista}
                  data-testid="submit-order-btn"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "A guardar..." : "Guardar Encomenda"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
